import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  filter,
  Subscription,
} from 'rxjs';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import {
  AuthConfig,
  OAuthErrorEvent,
  OAuthService,
  TokenResponse
} from 'angular-oauth2-oidc';
import jwt_decode from 'jwt-decode';

import { TokenService } from './token.service';
import { IAuthState, IToken } from '../models/auth';
import { SessionService } from './session.service';
import { environment } from 'src/environments/environment';

interface OAuthCustomError extends OAuthErrorEvent {
  params: {
    error: string | 'access_denied';
    error_description: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private refresherSubscription!: Subscription;
  private token = '';

  private authState = new BehaviorSubject<IAuthState>({
    message: 'Você não está autenticado, redirecionando para o login...',
    loading: true,
    denied: null,
    error: false
  });

  authState$ = this.authState.asObservable();

  constructor(
    private oauthService: OAuthService,
    private _router: Router,
    private _sessionService: SessionService,
    private _tokenService: TokenService,
  ) {
    this.init();
  }

  public async init() {
    this.oauthService.configure(this.buildAuthConfig());

    this.startAutoRefreshToken();
    this.isLogIn()
      ? this.setAuthStateLoggedIn()
      : this.tryLogin();
  }

  private buildAuthConfig(): AuthConfig {
    const serverUrl = environment.ssoServerUrl;

    return {
      issuer: `${serverUrl}/oauth2/token`,
      redirectUri: location.href,
      clientId: environment.ssoClientKey,
      scope: 'openid profile email',
      tokenEndpoint: `${serverUrl}/oauth2/token`,
      userinfoEndpoint: `${serverUrl}/oauth2/userinfo`,
      loginUrl: `${serverUrl}/oauth2/authorize`,
      requireHttps: false,
      disableAtHashCheck: true,
      responseType: 'id_token token',
      oidc: true,
      // Removido para evitar problemas de expiração antecipada do token.
      // O uso de `timeoutFactor` faz com que o evento `token_expires`
      // seja disparado antes da expiração real, com base na diferença entre
      // o tempo do navegador (local) e o tempo de emissão (`iat`).
      // Isso causava o token ser tratado como expirado logo após o login.
      // Por isso, deixamos sem timeoutFactor para considerar apenas o `exp` real.
      /* timeoutFactor: 0.95, */
      showDebugInformation: true,
      strictDiscoveryDocumentValidation: false
    };
  }

  startAutoRefreshToken(): void {
    this.refresherSubscription?.unsubscribe();

    this.refresherSubscription = this.oauthService.events
      .pipe(
        filter(
          (element: any) => {
            return element.type === 'token_expires' && element.info === 'access_token';
          }
        )
      )
      .subscribe((event) => {
        /*this.logTokenDetails(event, '🚨 token_expires DISPARADO');*/

        if (!this.oauthService.hasValidAccessToken()) {
          this.setActiveRouteAndTryLogin();
        }
      });
  }

  private logTokenDetails(event: any, context: string): void {
    const token = this.oauthService.getAccessToken();
    const payload = JSON.parse(atob(token.split('.')[1]));

    console.log(context);
    console.log(event.type, event.info);
    console.log('iat:', new Date(payload.iat * 1000).toISOString());
    console.log('exp:', new Date(payload.exp * 1000).toISOString());
    console.log('Agora (UTC):', new Date().toISOString());
  }

  private setAuthStateLoggedIn(): void {
    this.authState.next({
      message: 'Autenticado, clique no botão abaixo para ir ao início.',
      error: false,
      denied: false,
      loading: false
    });
  }

  tryLogin() {
    this.oauthService
      .tryLoginImplicitFlow()
      .then((isLoggedIn) => {

        if (this.oauthService.hasValidAccessToken()) {
          this.oauthService.tokenValidationHandler = new JwksValidationHandler();
        }

        if (!isLoggedIn) {
          this.oauthService.initImplicitFlow();
          return;
        }

        this.setAuthStateRedirecting();
        this.handleSuccessfulLogin();
      })
      .catch(error => this.handleLoginError(error));
  }

  private setAuthStateRedirecting(): void {
    this.authState.next({
      message: 'Autenticado! Redirecionando...',
      error: false,
      denied: false,
      loading: true
    });
  }

  private handleSuccessfulLogin(): void {
    this.token = this.oauthService.getAccessToken();
    this._tokenService.setTokenStorage(this.token);

    const roles = jwt_decode<IToken>(this.token)?.groups ?? [];
    this._sessionService.setRolesInSession(roles);

    const activeRoute = this._sessionService.getActiveRouteBeforeLogoutFromSession();

    setTimeout(() => {

      if (activeRoute) {
        this._router.navigate([activeRoute]);
        return;
      }

      if (this._router.url === '/' ||
          this._router.url.startsWith('/#access_token') ||
          this._router.url.startsWith('/autenticacao')) {
        this._router.navigate(['/esteira-apuracao']);
        return;
      }

    }, 500);
  }

  private handleLoginError(error: OAuthErrorEvent): void {
    const customError = error as OAuthCustomError;

    console.log(customError);

    if (customError.params?.error === 'access_denied') {
      this.authState.next({
        message: 'Acesso negado pelo usuário, por favor tente novamente.',
        error: true,
        denied: true,
        loading: false
      });
      return;
    }

    if (customError.type === 'token_error') {
      this._router.navigate(['/esteira-apuracao']);
      return;
    }

    const description = customError.params?.error_description || this.mapError(customError.params?.error);
    let mensagem = description == null ? "" : description;

    this.authState.next({
      message: `Houve um problema ao autenticar, por favor tente novamente ou informe o suporte.\n${mensagem.split('#')[0]}`,
      error: true,
      denied: false,
      loading: false
    });
  }

  private mapError(error: string | undefined): string {
    console.log(error);
    switch (error) {
      case 'unauthorized':
        return 'Você não tem permissão para acessar o sistema.';
      default:
        return error || 'Erro desconhecido.';
    }
  }

  isLogIn(): boolean {
    return !!this.oauthService.getAccessToken();
  }

  setActiveRouteAndTryLogin(): void {
    const activeRoute = this._router.url;
    this._sessionService.setActiveRouteBeforeLogoutFromSession(activeRoute);
    this.logout();
  }

  logout(): void {
    const idToken = this.oauthService.getIdToken();

    this.oauthService.logOut();

    const logoutUrl = `${environment.ssoServerUrl}/oauth2/logout?id_token_hint=${idToken}` +
      `&post_logout_redirect_uri=${encodeURIComponent(environment.ssoRedirectUri)}`;

    window.location.href = logoutUrl;
  }
}
